import { styled } from "styled-components";

import { colors, Divider, Skeleton, Text } from "@vericus/cadmus-ui";

import { Category } from "@/data/templates";

import { ClickableSubcategory } from "./clickable-sub-category";
import { SidebarButton } from "./sidebar-button";

export interface SidebarProps {
  categories: Category[];
  onDashboardClick: VoidFunction;
  onNewInstructionsClick: VoidFunction;
  onSubcategoryClick: (subcategoryId: string, subcategoryTitle: string) => void;
}

export const Sidebar = ({
  categories,
  onDashboardClick,
  onNewInstructionsClick,
  onSubcategoryClick,
}: SidebarProps) => {
  return (
    <Container>
      <CategoriesContainer>
        {categories.map((category) => (
          <CategoryComponent {...category} key={category.id}>
            {category.subcategories.map((subcategory) => (
              <ClickableSubcategory
                key={subcategory.id}
                isActive={subcategory.isSelected}
                name={subcategory.title}
                onClick={() =>
                  onSubcategoryClick(subcategory.id, subcategory.title)
                }
              />
            ))}
          </CategoryComponent>
        ))}
      </CategoriesContainer>
      <ActionsContainer>
        <Divider />
        <SidebarButton icon="Plus" onClick={onNewInstructionsClick}>
          New Instructions
        </SidebarButton>
        <SidebarButton icon="Home" onClick={onDashboardClick}>
          Dashboard
        </SidebarButton>
      </ActionsContainer>
    </Container>
  );
};

interface CategoryComponentProps extends Category {
  children?: JSX.Element[];
}

const CategoryComponent = ({ title, children }: CategoryComponentProps) => {
  return (
    <CategoryContainer aria-label={`${title} group`}>
      <SectionLabel kind="systemSm" as="div">
        {title}
      </SectionLabel>
      <SubcategoryContainer>{children}</SubcategoryContainer>
    </CategoryContainer>
  );
};

const SectionLabel = styled(Text)`
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${colors.grey500};
`;

const CategoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${colors.bgrey300};
  overflow: hidden;
  height: 100%;
  width: 100%;
`;

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 24px;
  padding-bottom: 24px;
  overflow-y: auto;
  scroll-behavior: smooth;
`;

const SubcategoryContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 16px 16px;
  gap: 4px;
`;

const CategorySkeleton = ({ subcategories }: { subcategories: number }) => {
  const subcategorySkeletons = Array(subcategories).fill("_");
  return (
    <div>
      <Skeleton width={40} />
      {subcategorySkeletons.map((_, index) => (
        <Skeleton width={90} key={index} />
      ))}
    </div>
  );
};

export const SidebarSkeleton = () => {
  return (
    <Container>
      <CategoriesContainer>
        <CategorySkeleton subcategories={7} />
        <CategorySkeleton subcategories={1} />
        <CategorySkeleton subcategories={1} />
        <CategorySkeleton subcategories={1} />
        <CategorySkeleton subcategories={1} />
      </CategoriesContainer>
    </Container>
  );
};
